import { ACSP_FAIL_REASON, DIRECTOR_CH_VERIFICATION_STATUS, type DirectorData } from "services/director/directorTypes";

export enum AcspFormOutcome {
  NotRequested = "NotRequested",
  /** Requested, but web-forms-sender hasn't sent the form to Companies House yet */
  Pending = "Pending",
  Sent = "Sent",
  WrongEmail = "WrongEmail",
  DuplicateIdentity = "DuplicateIdentity",
  /** A Hide My Email address, which Companies House can't use */
  ApplePrivateRelayEmail = "ApplePrivateRelayEmail",
  /** Failed for any other reason, or with none recorded - never counted as sent */
  Failed = "Failed",
}

type AcspFormFields = Partial<Pick<DirectorData, "identityVerificationStatus" | "identityVerificationFailReason">>;

/**
 * The status says whether the ACSP form has been sent; once it has, the fail reason says whether it
 * went through. A wrong email is also read off the status alone, since directors who failed before
 * the backend kept a reason carry `FAILED_WRONG_EMAIL` with no reason.
 */
export const getAcspFormOutcome = (director: AcspFormFields | undefined): AcspFormOutcome => {
  const status = director?.identityVerificationStatus;
  const failReason = director?.identityVerificationFailReason;

  // A reset clears the form, so the verification starts over
  if (!status || status === DIRECTOR_CH_VERIFICATION_STATUS.DEDUPLICATION_IN_PROGRESS) {
    return AcspFormOutcome.NotRequested;
  }
  if (status === DIRECTOR_CH_VERIFICATION_STATUS.SUBMITTED) {
    return AcspFormOutcome.Pending;
  }
  if (failReason === ACSP_FAIL_REASON.WRONG_EMAIL || status === DIRECTOR_CH_VERIFICATION_STATUS.FAILED_WRONG_EMAIL) {
    return AcspFormOutcome.WrongEmail;
  }
  if (failReason === ACSP_FAIL_REASON.DUPLICATE_IDENTITY) {
    return AcspFormOutcome.DuplicateIdentity;
  }
  if (failReason === ACSP_FAIL_REASON.APPLE_PRIVATE_RELAY_EMAIL) {
    return AcspFormOutcome.ApplePrivateRelayEmail;
  }
  if (
    failReason ||
    status === DIRECTOR_CH_VERIFICATION_STATUS.FAILED ||
    status === DIRECTOR_CH_VERIFICATION_STATUS.FAILED_DUPLICATED_IDENTITY
  ) {
    return AcspFormOutcome.Failed;
  }
  return AcspFormOutcome.Sent;
};
